import rehypeExternalLinks from 'rehype-external-links';
import rehypeHighlight from 'rehype-highlight';
import rehypeMinifyWhitespace from 'rehype-minify-whitespace';
import rehypeRaw from 'rehype-raw';
import rehypeSanitize, { defaultSchema } from 'rehype-sanitize';
import rehypeAttributes from 'rehype-attr';
import rehypeStringify from 'rehype-stringify';
import { remark } from 'remark';
import remarkBreaks from 'remark-breaks';
import remarkGfm from 'remark-gfm';
import remarkParse from 'remark-parse';
import remarkRehype from 'remark-rehype';
import stripMarkdown from 'strip-markdown';
import { merge } from '../../node_modules/ts-deepmerge/esm/index.js';
import { unified } from 'unified';
import { fixMalformedLists } from './plugins/fixMalformedLists.js';
import { fixSpacedStrong } from './plugins/fixSpacedStrong.js';
import rehypeAutoLinks from './plugins/rehypeAutoLinks.js';
import rehypeWrapTables from './plugins/rehypeWrapTables.js';
import { decodeNamedCharacterReference } from './plugins/decodeCharacterReference.js';

const emptyLineRegex = /^[ \t]*(\n|$)/gm;
const markdownToPlainText = async (mk, options) => {
  return remark().use(stripMarkdown, options).process(mk).then((res) => String(res));
};
const getMarkdownToMarkupProcessor = (options) => unified().use(remarkParse, {
  characterReferences: {
    decode: decodeNamedCharacterReference
  }
}).use(rehypeAutoLinks, {
  enabled: options.autoLinks ?? false
}).use(options.includeEmptyLines ? remarkBreaks : () => {
}).use(remarkRehype, {
  allowDangerousHtml: true
}).use(rehypeRaw).use(rehypeAttributes, {
  properties: "attr"
}).use(rehypeMinifyWhitespace).use(rehypeSanitize, merge(defaultSchema, {
  protocols: {
    href: [
      "fabric"
    ]
  },
  attributes: {
    a: [
      "href",
      "target",
      "rel",
      "class",
      "data-type",
      "data-resource-id",
      "data-ref-type",
      "data-ref-value"
    ],
    img: [
      "src",
      "alt"
    ]
  }
})).use(rehypeExternalLinks, {
  target: "_blank",
  rel: [
    "noopener",
    "noreferrer",
    "nofollow"
  ]
}).use(options.includeCodeHighlighting ? rehypeHighlight : () => {
}).use(rehypeWrapTables).use(rehypeStringify);
function parseMarkdown(markdown, options = {
  autoLinks: false,
  fixMalformedLists: false,
  fixSpacedStrong: false,
  includeEmptyLines: true,
  includeCodeHighlighting: false
}, createProcessor = getMarkdownToMarkupProcessor) {
  let treatedMarkdown = markdown;
  if (options.includeEmptyLines)
    treatedMarkdown = treatedMarkdown.replace(emptyLineRegex, " \n");
  if (options.fixMalformedLists) treatedMarkdown = fixMalformedLists(treatedMarkdown);
  if (options.fixSpacedStrong) treatedMarkdown = fixSpacedStrong(treatedMarkdown);
  const processor = createProcessor(options);
  return processor.processSync(treatedMarkdown).toString();
}
function parseMarkdownAsync(markdown, options = {
  fixMalformedLists: false,
  fixSpacedStrong: false,
  includeEmptyLines: true,
  includeCodeHighlighting: false
}) {
  return new Promise((resolve) => {
    let treatedMarkdown = markdown;
    if (options.includeEmptyLines)
      treatedMarkdown = treatedMarkdown.replace(emptyLineRegex, " \n");
    if (options.fixMalformedLists) treatedMarkdown = fixMalformedLists(treatedMarkdown);
    if (options.fixSpacedStrong) treatedMarkdown = fixSpacedStrong(treatedMarkdown);
    const processor = getMarkdownToMarkupProcessor(options);
    processor.process(treatedMarkdown, (err, file) => {
      if (err) {
        console.error(err);
        resolve("");
      } else {
        resolve(String(file));
      }
    });
  });
}
function parseInlineMarkdown(markdown) {
  const treatedMarkdown = markdown.replace(/\n/g, " ");
  const processor = unified().use(remarkParse).use(remarkGfm).use(remarkRehype, {
    allowDangerousHtml: true
  }).use(rehypeMinifyWhitespace).use(rehypeRaw).use(rehypeAttributes, {
    properties: "attr"
  }).use(rehypeSanitize, merge(defaultSchema, {
    protocols: {
      href: [
        "fabric"
      ],
      src: [
        "http",
        "https"
      ]
    },
    tagNames: [
      "a",
      "code",
      "em",
      "strong",
      "img",
      "u"
    ],
    attributes: {
      a: [
        "href",
        "target",
        "rel",
        "data-type",
        "data-resource-id",
        "data-ref-type",
        "data-ref-value"
      ],
      img: [
        "src",
        "alt"
      ]
    }
  })).use(rehypeExternalLinks, {
    target: "_blank",
    rel: [
      "noopener",
      "noreferrer",
      "nofollow"
    ]
  }).use(() => (tree) => {
    const walkTree = (node) => {
      if ("children" in node && Array.isArray(node.children)) {
        node.children = node.children.map((child) => {
          if (child.type === "element" && child.tagName === "img") {
            const src = child.properties.src;
            if (typeof src === "string" && src.endsWith("#no-mobile")) {
              child.properties["data-no-mobile"] = true;
              child.properties.src = src.replace("#no-mobile", "");
            }
          }
          return walkTree(child);
        });
      }
      return node;
    };
    return walkTree(tree);
  }).use(rehypeStringify);
  return processor.processSync(treatedMarkdown).toString();
}

export { markdownToPlainText, parseInlineMarkdown, parseMarkdown, parseMarkdownAsync };
//# sourceMappingURL=markdown.utils.js.map
